// PlanCampaignsSection.jsx — v119.123
// Shows all campaigns created from approved marketing-plan items, with a
// per-task Impact panel where HR/Marketing can set metric + target + actual.
// Rendered at the top of the Campaigns page so Khalid sees the direct
// consequence of approving a plan item.
import React, { useCallback, useEffect, useState } from "react";
import { api } from "./lib/api";
import { showToast } from "./lib/theme";
import { completionText, statusText } from "./marketing/campaignDisplay";

const STATUS_META = {
  pending:      { label: "⏳ لم تبدأ",  bg: "#fef3c7", fg: "#92400e" },
  in_progress:  { label: "🚀 قيد التنفيذ", bg: "#dbeafe", fg: "#1e40af" },
  done:         { label: "✅ منجزة",   bg: "#dcfce7", fg: "#166534" },
  blocked:      { label: "🚧 متعثّرة", bg: "#fee2e2", fg: "#991b1b" },
};

function fmt(n) {
  return new Intl.NumberFormat("ar-SA-u-nu-latn").format(Math.round(Number(n) || 0));
}

function TaskRow({ task, onChange }) {
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState({
    status:        task.status || "pending",
    impact_metric: task.impact_metric || "",
    impact_target: task.impact_target ?? "",
    impact_value:  task.impact_value ?? "",
    impact_note:   task.impact_note || "",
  });

  const dirty =
    local.status !== (task.status || "pending") ||
    local.impact_metric !== (task.impact_metric || "") ||
    String(local.impact_target ?? "") !== String(task.impact_target ?? "") ||
    String(local.impact_value ?? "") !== String(task.impact_value ?? "") ||
    local.impact_note !== (task.impact_note || "");

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...local };
      body.impact_target = body.impact_target === "" ? null : Number(body.impact_target);
      body.impact_value  = body.impact_value  === "" ? null : Number(body.impact_value);
      const r = await api.patch(`/marketing-plans/campaign-tasks/${task.id}`, body);
      onChange?.(r.data.task);
      showToast("✅ تم حفظ التقدّم", "success");
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل الحفظ", "danger");
    }
    setSaving(false);
  };

  const st = STATUS_META[local.status] || STATUS_META.pending;
  const pct = local.impact_target && local.impact_value != null
    ? Math.round((Number(local.impact_value) / Number(local.impact_target)) * 100)
    : null;

  return (
    <div data-testid={`camp-task-${task.id}`} style={{
      padding: 10, background: "#fff", borderRadius: 8,
      border: `1px solid ${dirty ? "#C9A227" : "#e5e5e5"}`,
      display: "grid", gap: 6, marginBottom: 6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 700, color: "var(--brown-deep)", fontSize: 13 }}>{task.title}</div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
            👤 {task.assigned_to || "—"}
            {task.channel && ` · 📡 ${task.channel}`}
            {task.deadline && ` · 📅 ${task.deadline}`}
          </div>
          {task.description && (
            <div data-testid={`camp-task-${task.id}-description`}
                  style={{ fontSize: 11.5, color: "#3E2723", marginTop: 4,
                             padding: "5px 8px", background: "#fef9e7",
                             borderRadius: 4, lineHeight: 1.5 }}>
              📝 {task.description}
            </div>
          )}
          {task.target_audience && (
            <div data-testid={`camp-task-${task.id}-audience`}
                  style={{ fontSize: 11, color: "#7B5804", marginTop: 4, fontWeight: 700 }}>
              🎯 الفئة المستهدفة: {task.target_audience}
            </div>
          )}
        </div>
        <span style={{ background: st.bg, color: st.fg, padding: "3px 10px",
                        borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
          {st.label}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
        <select value={local.status} onChange={(e) => setLocal({ ...local, status: e.target.value })}
          data-testid={`camp-task-${task.id}-status`}
          style={{ padding: 5, border: "1px solid #ddd", borderRadius: 4, fontSize: 12 }}>
          <option value="pending">⏳ لم تبدأ</option>
          <option value="in_progress">🚀 قيد التنفيذ</option>
          <option value="done">✅ منجزة</option>
          <option value="blocked">🚧 متعثّرة</option>
        </select>
        <input type="text" placeholder="📊 المقياس" value={local.impact_metric}
          onChange={(e) => setLocal({ ...local, impact_metric: e.target.value })}
          data-testid={`camp-task-${task.id}-metric`}
          style={{ padding: 5, border: "1px solid #ddd", borderRadius: 4, fontSize: 12 }} />
        <input type="number" placeholder="🎯 المستهدف" value={local.impact_target ?? ""}
          onChange={(e) => setLocal({ ...local, impact_target: e.target.value })}
          data-testid={`camp-task-${task.id}-target`}
          style={{ padding: 5, border: "1px solid #ddd", borderRadius: 4, fontSize: 12 }} />
        <input type="number" placeholder="✅ الفعلي" value={local.impact_value ?? ""}
          onChange={(e) => setLocal({ ...local, impact_value: e.target.value })}
          data-testid={`camp-task-${task.id}-value`}
          style={{ padding: 5, border: "1px solid #ddd", borderRadius: 4, fontSize: 12 }} />
      </div>
      {pct != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, height: 5, background: "#f0f0f0", borderRadius: 3 }}>
            <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%",
                            background: pct >= 100 ? "#10B981" : pct >= 70 ? "#C9A227" : "#EF4444",
                            borderRadius: 3 }} />
          </div>
          <div style={{ fontSize: 11, fontWeight: 700,
                          color: pct >= 100 ? "#10B981" : pct >= 70 ? "#C9A227" : "#EF4444" }}>
            {pct}% من المستهدف
          </div>
        </div>
      )}
      {dirty && (
        <div>
          <button type="button" onClick={save} disabled={saving}
            data-testid={`camp-task-${task.id}-save`}
            style={{ padding: "4px 14px", background: "#C9A227", color: "#3E2723",
                      border: "none", borderRadius: 4, fontWeight: 800, fontSize: 11,
                      cursor: saving ? "wait" : "pointer" }}>
            {saving ? "⏳ يحفظ..." : "💾 احفظ التقدّم"}
          </button>
        </div>
      )}
    </div>
  );
}

function CodeLinkPanel({ camp, onSaved }) {
  const [codes, setCodes] = useState(null);
  const [sel, setSel] = useState(() => new Set(camp.retm_discount_ids || []));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/marketing-plans/campaigns/retm-codes")
      .then(({ data }) => setCodes(data.items || []))
      .catch(() => setCodes([]));
  }, []);

  const toggleCode = (id) => setSel((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/marketing-plans/campaigns/${camp.id}`, { retm_discount_ids: [...sel] });
      showToast("✅ تم ربط الأكواد بالحملة", "success");
      onSaved?.();
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل الحفظ", "danger");
    }
    setSaving(false);
  };

  return (
    <div data-testid={`camp-${camp.id}-codes-panel`}
         style={{ marginTop: 10, background: "#FBF7F0", padding: 10, borderRadius: 8, border: "1px solid #E8DFD0" }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 6 }}>
        🔗 اربط أكواد خصم RETM بهذه الحملة — فواتير هذه الأكواد داخل فترة الحملة تُحسب كإيراد مباشر (ROI)
      </div>
      {codes === null ? (
        <div style={{ fontSize: 12, color: "var(--muted)", padding: 8 }}>⏳ يحمّل الأكواد...</div>
      ) : codes.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--muted)", padding: 8 }}>
          لا توجد أكواد خصم RETM مسجّلة بعد — أنشئ الخصم في RETM أولاً ثم عد هنا.
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 160, overflowY: "auto" }}>
          {codes.map((c) => (
            <button key={c.id} type="button" onClick={() => toggleCode(c.id)}
              data-testid={`camp-${camp.id}-code-${c.id}`}
              style={{
                padding: "5px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
                border: sel.has(c.id) ? "1.5px solid #3E2723" : "1px solid #D9CDB8",
                background: sel.has(c.id) ? "#3E2723" : "#fff",
                color: sel.has(c.id) ? "#fff" : "var(--brown-deep)",
              }}>
              {sel.has(c.id) ? "✓ " : ""}{c.name}
            </button>
          ))}
        </div>
      )}
      <button type="button" onClick={save} disabled={saving}
        data-testid={`camp-${camp.id}-codes-save`}
        style={{ marginTop: 8, padding: "6px 16px", background: "#C9A227", color: "#3E2723",
                 border: "none", borderRadius: 6, fontWeight: 800, fontSize: 12,
                 cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>
        {saving ? "⏳ يحفظ..." : `💾 حفظ الربط (${sel.size})`}
      </button>
    </div>
  );
}

function CampaignCard({ camp, onReload }) {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [codesOpen, setCodesOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/marketing-plans/campaigns/${camp.id}/tasks`);
      setTasks(data.items || []);
    } catch (e) {
      showToast("فشل تحميل المهام", "danger");
    }
    setLoading(false);
  }, [camp.id]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && tasks.length === 0) await loadTasks();
  };

  const impactAchieved = camp.impact_achieved_pct;

  return (
    <div data-testid={`plan-camp-${camp.id}`} style={{
      padding: 12, background: "#fff", borderRadius: 10,
      borderInlineStart: "4px solid #C9A227",
      marginBottom: 10,
      boxShadow: "0 2px 6px rgba(0,0,0,.04)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 800, color: "var(--brown-deep)", fontSize: 14 }}>
            🎯 {camp.name}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 3 }}>
            📅 {camp.start_date || "—"} → {camp.end_date || "—"}
            {camp.budget > 0 && ` · 💰 ${fmt(camp.budget)} ر.س`}
            · 👥 {(camp.owners || []).join("، ") || "—"}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 5 }}>
            <span data-testid={`camp-${camp.id}-status`} style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999,
              background: camp.status === "ended" ? "#F3F4F6" : camp.status === "scheduled" ? "#EFF6FF" : "#ECFDF5",
              color: camp.status === "ended" ? "#6B7280" : camp.status === "scheduled" ? "#1e40af" : "#059669" }}>
              {camp.status_label || statusText(camp)}
            </span>
            {camp.is_delayed && (
              <span data-testid={`camp-${camp.id}-delayed`} style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: "#FEF2F2", color: "#DC2626" }}>⏳ متأخرة</span>
            )}
            {camp.possible_duplicate && (
              <span data-testid={`camp-${camp.id}-duplicate`} style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: "#FFFBEB", color: "#B45309" }}>⚠ مكرر محتمل</span>
            )}
          </div>
          {(camp.channels || []).length > 0 && (
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              📡 {camp.channels.join(" · ")}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ textAlign: "center", background: "#FBF7F0", padding: "6px 12px", borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700 }}>الإنجاز</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: (camp.progress_pct ?? 0) >= 80 ? "#10B981" : "var(--brown-deep)" }}>
              {camp.no_tasks || camp.progress_pct == null ? completionText(camp) : `${camp.progress_pct}%`}
            </div>
            <div style={{ fontSize: 10, color: "var(--muted)" }}>
              {camp.tasks_done} من {camp.tasks_total}
              {camp.tasks_in_progress > 0 && ` · جارية ${camp.tasks_in_progress}`}
            </div>
            {camp.time_elapsed_pct != null && (
              <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 1 }}>الوقت المنقضي {camp.time_elapsed_pct}%</div>
            )}
          </div>
          {impactAchieved != null && (
            <div style={{ textAlign: "center", background: "#f0f9ff", padding: "6px 12px", borderRadius: 8 }}>
              <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700 }}>الأثر</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: impactAchieved >= 100 ? "#10B981" : "#1e40af" }}>
                {impactAchieved}%
              </div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>من المستهدف</div>
            </div>
          )}
          {camp.code_roi && (
            <div data-testid={`camp-${camp.id}-code-roi`}
                 style={{ textAlign: "center", background: "#FDF6E3", padding: "6px 12px", borderRadius: 8,
                          border: "1px solid #C9A227" }}>
              <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700 }}>ROI الأكواد</div>
              <div style={{ fontSize: 15, fontWeight: 800,
                            color: (camp.code_roi.roi_pct ?? 0) >= 0 ? "#10B981" : "#DC2626" }}>
                {camp.code_roi.roi_pct != null ? `${camp.code_roi.roi_pct}%` : "—"}
              </div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>
                {camp.code_roi.uses} استخدام · {fmt(camp.code_roi.revenue)} ر.س
              </div>
            </div>
          )}
          <button type="button" onClick={() => setCodesOpen((v) => !v)}
            data-testid={`plan-camp-${camp.id}-codes-toggle`}
            style={{ padding: "6px 12px", background: "#fff", color: "#3E2723",
                      border: "1.5px solid #C9A227", borderRadius: 6, fontWeight: 700,
                      fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
            🔗 أكواد RETM{(camp.retm_discount_ids || []).length ? ` (${camp.retm_discount_ids.length})` : ""}
          </button>
          <button type="button" onClick={toggle} data-testid={`plan-camp-${camp.id}-toggle`}
            style={{ padding: "6px 12px", background: "#3E2723", color: "#fff",
                      border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            {open ? "🔼 إخفاء المهام" : "📋 المهام + الأثر"}
          </button>
        </div>
      </div>
      {codesOpen && <CodeLinkPanel camp={camp} onSaved={() => { setCodesOpen(false); onReload?.(); }} />}
      {open && (
        <div style={{ marginTop: 10, background: "#FAFAFA", padding: 8, borderRadius: 6 }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "var(--muted)", padding: 12, fontSize: 12 }}>⏳ يحمّل المهام...</div>
          ) : tasks.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--muted)", padding: 12, fontSize: 12 }}>
              لا مهام مسجّلة تحت هذه الحملة.
            </div>
          ) : tasks.map((t) => (
            <TaskRow key={t.id} task={t} onChange={(updated) => {
              setTasks((prev) => prev.map((x) => x.id === updated.id ? updated : x));
              onReload?.();
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlanCampaignsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing-plans/campaigns");
      setRows(data.items || []);
    } catch (e) {
      // ok — user may not have permission
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (rows.length === 0) return null;   // Hide section if empty

  return (
    <div data-testid="plan-campaigns-section" style={{ marginBottom: 22 }}>
      <div style={{
        padding: "12px 16px", borderRadius: 10, marginBottom: 10,
        background: "linear-gradient(135deg,#FBF7F0,#fff)",
        border: "1px solid #C9A227",
      }}>
        <div style={{ fontWeight: 800, color: "var(--brown-deep)", fontSize: 14, marginBottom: 2 }}>
          📊 الحملات من الخطط التسويقية المعتمدة ({rows.length})
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)" }}>
          هذه الحملات تولّدت تلقائياً من اعتماد بنود الخطة التسويقية. سجّل هنا تقدّم كل مهمة + قيّم الأثر مقابل المستهدف.
        </div>
      </div>
      {rows.map((c) => (
        <CampaignCard key={c.id} camp={c} onReload={load} />
      ))}
    </div>
  );
}
